import { useMemo, useState } from "react";
import { Box, Button, Flex, IconButton, Text } from "@radix-ui/themes";
import * as Popover from "@radix-ui/react-popover";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { enGB } from "date-fns/locale";

type DateFieldProps = {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
};

// Popover-without-Portal so it works inside Dialog (CLAUDE.md gotcha).
export default function DateField({
  value,
  onChange,
  placeholder = "Pick a date",
  disabled,
  minDate,
  maxDate,
}: DateFieldProps) {
  const selected = value ? parseISO(value) : null;
  const [open, setOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(selected ?? new Date());

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 1, locale: enGB });
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 1, locale: enGB });
    return eachDayOfInterval({ start, end });
  }, [viewMonth]);

  const minD = minDate ? parseISO(minDate) : null;
  const maxD = maxDate ? parseISO(maxDate) : null;

  const isOutOfRange = (d: Date) =>
    (minD && d < new Date(minD.getFullYear(), minD.getMonth(), minD.getDate())) ||
    (maxD && d > new Date(maxD.getFullYear(), maxD.getMonth(), maxD.getDate()));

  const pickDay = (d: Date) => {
    if (isOutOfRange(d)) return;
    onChange(format(d, "yyyy-MM-dd"));
  };

  const clear = () => {
    onChange("");
    setOpen(false);
  };

  return (
    <Popover.Root open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="surface"
          disabled={disabled}
          style={{
            width: "100%",
            justifyContent: "space-between",
            fontWeight: "normal",
          }}
        >
          <Flex align="center" gap="2">
            <Calendar size={14} />
            <Text size="2" color={selected ? undefined : "gray"}>
              {selected ? format(selected, "d MMM yyyy") : placeholder}
            </Text>
          </Flex>
          {selected && !disabled && (
            <Box
              role="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange("");
              }}
              style={{ display: "flex", color: "var(--gray-10)" }}
              aria-label="Clear date"
            >
              <X size={14} />
            </Box>
          )}
        </Button>
      </Popover.Trigger>
      <Popover.Content
        align="start"
        sideOffset={4}
        style={{
          width: 280,
          padding: 12,
          zIndex: 12000,
          backgroundColor: "var(--color-panel-solid)",
          border: "1px solid var(--gray-a5)",
          borderRadius: "var(--radius-3)",
          boxShadow: "var(--shadow-4)",
        }}
      >
        <Flex justify="between" align="center" mb="2">
          <IconButton
            variant="ghost"
            size="1"
            onClick={() => setViewMonth(subMonths(viewMonth, 1))}
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </IconButton>
          <Text size="2" weight="medium">
            {format(viewMonth, "LLLL yyyy")}
          </Text>
          <IconButton
            variant="ghost"
            size="1"
            onClick={() => setViewMonth(addMonths(viewMonth, 1))}
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </IconButton>
        </Flex>

        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 2,
            marginBottom: 4,
          }}
        >
          {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
            <Text key={d} size="1" color="gray" align="center">
              {d}
            </Text>
          ))}
        </Box>

        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 2,
          }}
        >
          {days.map((d) => {
            const inMonth = isSameMonth(d, viewMonth);
            const isSelected = selected && isSameDay(d, selected);
            const isToday = isSameDay(d, new Date());
            const outOfRange = isOutOfRange(d);
            return (
              <button
                key={d.toISOString()}
                type="button"
                disabled={!!outOfRange}
                onClick={() => pickDay(d)}
                style={{
                  height: 30,
                  borderRadius: 6,
                  border: isToday ? "1px solid var(--accent-7)" : "1px solid transparent",
                  backgroundColor: isSelected ? "var(--accent-9)" : "transparent",
                  color: isSelected
                    ? "var(--accent-contrast)"
                    : inMonth
                    ? "var(--gray-12)"
                    : "var(--gray-8)",
                  cursor: outOfRange ? "not-allowed" : "pointer",
                  opacity: outOfRange ? 0.4 : 1,
                  fontSize: 13,
                  fontWeight: isSelected ? 600 : 400,
                }}
              >
                {format(d, "d")}
              </button>
            );
          })}
        </Box>

        <Flex justify="between" align="center" mt="3">
          <Button
            type="button"
            variant="ghost"
            size="1"
            onClick={clear}
            disabled={!selected}
          >
            Clear
          </Button>
          <Button type="button" size="1" onClick={() => setOpen(false)}>
            Done
          </Button>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
